// Error reporting (Sentry). Off unless VITE_SENTRY_DSN is set; the SDK then loads as its own chunk,
// so a build without a DSN ships none of it. Events are tagged with the environment (production,
// sandbox, development) and the release (the commit CI built). See docs/operations/monitoring.md.
import type { MonitoringConfig } from './auth/config';

type Sentry = typeof import('@sentry/react');

let sentry: Promise<Sentry | undefined> | undefined;

/** Starts error reporting once per page; a no-op without a DSN. */
export function startMonitoring(config: MonitoringConfig | undefined): void {
  if (!config || sentry) return;
  sentry = import('@sentry/react').then(
    (Sentry) => {
      Sentry.init({
        dsn: config.dsn,
        environment: config.environment,
        release: config.release,
        // Errors only (no tracing, no replay) and no personal data: no user details, cookies, headers
        // or bodies, and no query strings (auth callbacks carry one-time codes there).
        dataCollection: {
          userInfo: false,
          cookies: false,
          httpHeaders: false,
          httpBodies: [],
          urlQueryParams: false,
        },
      });
      return Sentry;
    },
    // Reporting must never break the app (an ad blocker can refuse the chunk).
    () => undefined,
  );
}

/** Reports an error the app caught itself (React Router's onError, for instance). */
export function captureError(error: unknown): void {
  void sentry?.then((Sentry) => Sentry?.captureException(error));
}
